import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useEditionAwareness } from '../services/edition-awareness-context';
import { useGetExperimentValue } from '../services/experiment-context';
import { isEligibleForAutomationFreeLimitModal } from '../services/is-eligible-for-automation-free-limit-modal';
import { isEligibleForConfluenceBuyReverseTrialExperiment } from '../services/is-eligible-for-confluence-buy-reverse-trials';
import { isEligibleForConfluencePremiumPopup } from '../services/is-eligible-for-confluence-premium-popup';
import { isEligibleForConfluenceStandardAddPaymentModal } from '../services/is-eligible-for-confluence-standard-add-payment-modal';
import { isEligibleForEAtoOSBFreeWithPDE } from '../services/is-eligible-for-ea-to-osb-free-with-pde';
import { isEligibleForEAtoOSBPremiumNonRTExperiment } from '../services/is-eligible-for-ea-to-osb-premium-non-rt-experiment';
import { isEligibleForExploreEditionBenefitsModalStorageLimit } from '../services/is-eligible-for-explore-edition-benefits-modal-storage-limit';
import { isEligibleForExploreEditionBenefitsModalWithPDE } from '../services/is-eligible-for-explore-edition-benefits-modal-with-pde';
import { isEligibleForExploreEditionBenefitsModalWithoutPDE } from '../services/is-eligible-for-explore-edition-benefits-modal-without-pde';
import { isEligibleForFreeToStandardModal } from '../services/is-eligible-for-free-to-standard-modal';
import { isEligibleForJiraPremiumPredunning } from '../services/is-eligible-for-jira-premium-predunning';
import { isEligibleForJiraStandardPredunning } from '../services/is-eligible-for-jira-standard-predunning';
import { isEligibleForPremiumAddPaymentModal } from '../services/is-eligible-for-premium-add-payment-modal';
import { isEligibleForPremiumPaymentDetailsPopup } from '../services/is-eligible-for-premium-payment-details-popup';
import { isEligibleForReverseTrial } from '../services/is-eligible-for-reverse-trial';
import { isEligibleForReverseTrialPlanSelection } from '../services/is-eligible-for-reverse-trial-plan-selection';
import { isEligibleForServCoUplift } from '../services/is-eligible-for-service-collection-uplift';
import { isEligibleForStandardAddPaymentModal } from '../services/is-eligible-for-standard-add-payment-modal';
import { isEligibleForStandardPaymentDetailsPopup } from '../services/is-eligible-for-standard-payment-details-popup';
import { isEligibleForStandardPaymentFlowLink } from '../services/is-eligible-for-standard-payment-flow-link';
import { isEligibleForStorageHardLimit } from '../services/is-eligible-for-storage-hard-limit';
import { isEligibleForTWCAddPaymentDetails } from '../services/is-eligible-for-twc-add-payment-details';
import { isEligibleForTWCChangePlan } from '../services/is-eligible-for-twc-change-plan';
import { isEligibleForTWCPlanSelectionPage } from '../services/is-eligible-for-twc-plan-selection-page';
import { isEligibleForTWCTrialNoPDE } from '../services/is-eligible-for-twc-trial-no-pde';
import { isProductOneOf } from '../services/is-product-one-of';

import { AutomationFreeLimitModal } from './dialogs/automation-free-limit-modal';
import { CollectionAddPaymentDetailsPopup } from './dialogs/collection-add-payment-details-popup';
import { CollectionChangePlanPopup } from './dialogs/collection-change-plan-popup';
import { ExploreEditionBenefitsModal } from './dialogs/explore-edition-benefits-modal/explore-edition-benefits-modal';
import { FreeToStandardModal } from './dialogs/free-to-standard-modal';
import { PaymentDetailsPopup } from './dialogs/payment-details-popup';
import { PlanSelectionPageModal } from './dialogs/plan-selection-page-modal';
import { PremiumAddPaymentModal } from './dialogs/premium-add-payment-modal';
import { PremiumPopup } from './dialogs/premium-popup';
import { ReverseTrialPopupDialog } from './dialogs/reverse-trial-popup';
import { StandardAddPaymentModal } from './dialogs/standard-add-payment-modal';
import { StorageHardLimitModal } from './dialogs/storage-hard-limit-modal';
import { PaymentFlowLink } from './links/payment-flow-link';
import { ServiceCollectionLink } from './links/service-collection-link';
import { UpgradeStandardFlowLink } from './links/upgrade-standard-flow-link';

function FreeEdition() {
	const context = useEditionAwareness();
	const { automationFreeLimitStatus } = context;
	const { getExperimentValue } = useGetExperimentValue();

	if (isEligibleForTWCPlanSelectionPage(context)) {
		return <PlanSelectionPageModal type="twc" />;
	}

	if (fg('twcg-1076_jira_automation_limits_kill_switch')) {
		if (isEligibleForAutomationFreeLimitModal(context)) {
			// These two statuses change the button content, and we need to fire exposure
			const fireExperimentExposure =
				automationFreeLimitStatus === 'BREACHED' ||
				automationFreeLimitStatus === 'PROJECTED_TO_BREACH';

			const cohort = getExperimentValue({
				statsigParams: {
					experimentName: 'twcg-1076_jira_automation_limits_experiment',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
					options: {
						fireExperimentExposure,
					},
				},
				trackEventParameters: {
					trackEventGrowthInitiative: 'luna',
				},
			});

			if (cohort === 'variation') {
				return <AutomationFreeLimitModal />;
			}
		}
	}

	if (isEligibleForStorageHardLimit(context)) {
		return <StorageHardLimitModal />;
	}

	if (isEligibleForEAtoOSBFreeWithPDE(context)) {
		const cohort = getExperimentValue({
			statsigParams: {
				experimentName: 'ea-to-osb-free-with-pde-ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
				options: {
					fireExperimentExposure: false,
				},
			},
		});

		if (cohort === 'variation') {
			return <UpgradeStandardFlowLink />;
		}
	}

	if (isEligibleForFreeToStandardModal(context)) {
		return <FreeToStandardModal />;
	}

	return null;
}

function StandardEdition() {
	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();
	const {
		product,
		isInTrial,
		invoiceableStatus,
		canAddPaymentDetails,
		hasPaymentDetailsEntered,
		currentRoute,
	} = context;

	if (isEligibleForTWCTrialNoPDE(context)) {
		// Teamwork Collection with no PDE in trial
		return <PlanSelectionPageModal type="twc" />;
	}

	if (isEligibleForTWCChangePlan(context)) {
		return <CollectionChangePlanPopup />;
	}

	if (isEligibleForTWCAddPaymentDetails(context)) {
		return <CollectionAddPaymentDetailsPopup />;
	}

	// Service Collection Uplift for JSM Standard
	if (isEligibleForServCoUplift(context)) {
		return <ServiceCollectionLink />;
	}

	if (
		isInTrial &&
		isProductOneOf(product, ['jira-service-management', 'jira-customer-service']) &&
		(hasPaymentDetailsEntered ||
			invoiceableStatus === 'INVOICEABLE' ||
			(invoiceableStatus === 'NOT_INVOICEABLE' && canAddPaymentDetails))
	) {
		getExperimentValue({
			statsigParams: {
				experimentName: 'jsmgrowth-466_aa_test',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
				options: {
					fireExperimentExposure: true,
				},
			},
			trackEventParameters: {
				trackEventGrowthInitiative: 'sequoia',
			},
		});
	}

	// Storage-limit auto-upgrade path is independent of the STAR experiment / its
	// killswitch / cohort. Render the modal whenever the dedicated storage-limit
	// killswitch is on and the entitlement trial trigger is AUTO_UPGRADE_STORAGE_LIMIT.
	if (isEligibleForExploreEditionBenefitsModalStorageLimit(context)) {
		return <ExploreEditionBenefitsModal />;
	}

	if (isEligibleForExploreEditionBenefitsModalWithPDE(context)) {
		if (
			getExperimentValue({
				statsigParams: {
					experimentName: 'jsmgrowth466_star',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
				trackEventParameters: {
					trackEventGrowthInitiative: 'sequoia',
				},
			}) === 'variation'
		) {
			return <ExploreEditionBenefitsModal />;
		}
		return null;
	}

	if (isEligibleForExploreEditionBenefitsModalWithoutPDE(context)) {
		if (
			getExperimentValue({
				statsigParams: {
					experimentName: 'jsmgrowth466_star',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
				trackEventParameters: {
					trackEventGrowthInitiative: 'sequoia',
				},
			}) === 'variation'
		) {
			return <ExploreEditionBenefitsModal />;
		}
		// jira-service-management intentionally falls through to the downstream isEligibleForStandardAddPaymentModal handler
	}

	// Catch-all: block CSM and ops routes that fell through (e.g. killswitch OFF, not in trial, or non-variation cohort)
	if (product === 'jira-customer-service' || currentRoute === 'ops') {
		return null;
	}

	if (isEligibleForStandardPaymentFlowLink(context)) {
		return <PaymentFlowLink />;
	}

	if (isEligibleForStandardAddPaymentModal(context)) {
		return <StandardAddPaymentModal />;
	}

	// Jira Standard - Site admins get routed to JSTO, others to payment popup
	if (isEligibleForStandardPaymentDetailsPopup(context)) {
		return <PaymentDetailsPopup />;
	}

	if (isEligibleForConfluenceStandardAddPaymentModal(context)) {
		return <StandardAddPaymentModal />;
	}

	if (isEligibleForJiraStandardPredunning(context)) {
		return <PaymentFlowLink />;
	}

	return null;
}

function PremiumEdition() {
	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();

	// make sure twc is always shown first before jira reverse trial
	// otherwise, jira reverse trial will be shown with collection

	// additional check on invoiceableStatus and canAddPaymentDetails
	// to block any EA to be shown if the customer is in a collection
	// * confluence premium is showing EA regardless which needs to be blocked from collection
	if (isEligibleForTWCTrialNoPDE(context)) {
		// Teamwork Collection with no PDE in trial
		return <PlanSelectionPageModal type="twc" />;
	}

	if (isEligibleForTWCChangePlan(context)) {
		return <CollectionChangePlanPopup />;
	}

	if (isEligibleForTWCAddPaymentDetails(context)) {
		return <CollectionAddPaymentDetailsPopup />;
	}

	// Service Collection Uplift for JSM Premium
	if (isEligibleForServCoUplift(context)) {
		return <ServiceCollectionLink />;
	}

	if (isEligibleForReverseTrialPlanSelection(context)) {
		const rtCohort = getExperimentValue({
			statsigParams: {
				experimentName: 'jira_reverse_trial_ea_updates_during_trial',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
			trackEventParameters: {
				trackEventGrowthInitiative: 'transmuters',
			},
		});

		if (rtCohort === 'variation') {
			return <PlanSelectionPageModal type="reverse-trial" />;
		}
	}

	if (isEligibleForReverseTrial(context)) {
		return <ReverseTrialPopupDialog />;
	}

	if (isEligibleForEAtoOSBPremiumNonRTExperiment(context)) {
		const cohort = getExperimentValue({
			statsigParams: {
				experimentName: 'ea-to-osb-premium-non-rt-ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
				options: {
					fireExperimentExposure: false,
				},
			},
		});

		if (cohort === 'variation') {
			return <PaymentFlowLink />;
		}
	}

	if (isEligibleForPremiumPaymentDetailsPopup(context)) {
		return <PaymentDetailsPopup />;
	}

	if (isEligibleForJiraPremiumPredunning(context)) {
		return <PaymentFlowLink />;
	}

	if (
		isEligibleForConfluenceBuyReverseTrialExperiment(context) &&
		fg('growth_confluence_buy_reverse_trials_fg')
	) {
		const abCohort = getExperimentValue({
			statsigParams: {
				experimentName: 'growth_confluence_buy_reverse_trials_ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
			trackEventParameters: {
				trackEventGrowthInitiative: 'luna',
				trackEventAttributes: {
					touchpointId: 'actions-and-content',
				},
			},
		});

		if (abCohort === 'variation') {
			return <PlanSelectionPageModal type="reverse-trial" />;
		}
	}

	if (isEligibleForConfluencePremiumPopup(context)) {
		return <PremiumPopup />;
	}

	if (isEligibleForPremiumAddPaymentModal(context)) {
		return <PremiumAddPaymentModal />;
	}

	return null;
}

/**
 * Covers what button and action we show to the customer.
 *
 * Experiments here should just modify what is shown to the customer not if it is shown to the customer.
 */
export function ActionsAndContent(): React.JSX.Element | null {
	const { edition } = useEditionAwareness();

	if (edition === 'free') {
		return <FreeEdition />;
	}

	if (edition === 'standard') {
		return <StandardEdition />;
	}

	if (edition === 'premium') {
		return <PremiumEdition />;
	}

	return null;
}
